import classNames from 'classnames';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { useDiveContext } from '../../../context';
import { checkIsSSR, throttle } from '../../../helpers/helpers';
import { DiveBreakpoint } from '../../../types/Dive.types';
import { DiNav } from '../../DiNav/DiNav';
import { DiNavDropdown } from '../../DiNav/DiNavDropdown/DiNavDropdown';
import HeaderNavDropdownItem from '../HeaderNavDropdownItem/HeaderNavDropdownItem';
import HeaderNavItem from '../HeaderNavItem/HeaderNavItem';
import { headerListCVA, headerNavCVA } from './HeaderNav.cva';
import type {
  HeaderNavOverflowVariant,
  HeaderNavProps,
} from './HeaderNav.types';

// Header height 64px => Trigger height 38px => 64 - 38 = 26px / 2 = 13px
// To align the dropdown with the bottom of the header
const DROPDOWN_OFFSET_OPTIONS = { offset: { mainAxis: 13 } };

const REFRESH_CONFIG_THRESHOLD = 300;

/**
 * Horizontal list of navigation items to be used in the header. It can contain a mix of `HeaderNavItem` and `HeaderNavDropdown` depending on the variant.
 *
 * It can be used in two variants:
 * - `'dropdown'`: Overflowing items are moved into a dropdown menu (desktop and tablet only).
 * - `'scroll'`: Items remain on a single line and are horizontally scrollable (default mobile behavior).
 *
 * ### Accessibility:
 * - **ARIA:** `role="menubar"` on the `<ul />` and `role="menuitem"` on the items.
 * - **Tab and Shift+Tab:** `<ul />` is considered a single tab stop. Only the menubar itself is tabbable. The items are accessible via arrow keys.
 * - **Navigation keys:** ArrowKeys (`Up`, `Down`, `Left`, `Right`) to navigate between items inside.
 * - **Press:** (`Enter` or `Space`) to select/click an item.
 *
 * @example
 *
 * ```tsx
 * <HeaderNav
 *   items={menuItems}
 *   activeIndex={0} // first item is active
 *   dropdownLabel="More"
 *   variant="scroll"
 * />
 * ```
 */
export function HeaderNav({
  className,
  isTransparent,
  variant = 'dropdown',
  dropdownLabel = 'More',
  items,
  activeIndex,
  shouldCenterActiveItem: userPrefersCentering = true,
  ...props
}: HeaderNavProps): JSX.Element {
  const { isTv, device } = useDiveContext();
  const [hasDropdown, setHasDropdown] = useState(false);

  const getHeaderNavConfigFn = useCallback(
    () =>
      getHeaderNavConfig({
        isTv: !!isTv,
        userPrefersCentering: userPrefersCentering,
        overflowVariant: variant,
      }),
    [isTv, userPrefersCentering, variant],
  );

  const { shouldCenterActiveItem } = getHeaderNavConfigFn();

  useEffect(() => {
    if (isTv) {
      return;
    }
    const handleResize = throttle(() => {
      const { hasDropdown } = getHeaderNavConfigFn();
      setHasDropdown(hasDropdown);
    }, REFRESH_CONFIG_THRESHOLD);
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getHeaderNavConfigFn, isTv]);

  const hasOverflowGradients = !isTransparent && !isTv && !hasDropdown;

  return (
    <DiNav
      className={classNames(headerNavCVA({ hasOverflowGradients }), className)}
      dropdown={
        <DiNavDropdown
          dropdownMenuClassName={classNames([
            'bg-dt-surface-profile-card-profile-card rounded-dt-radius-large',
            'py-dt-spacing-100 shadow-card w-max outline-none',
          ])}
          options={DROPDOWN_OFFSET_OPTIONS}
          label={dropdownLabel}
          trigger={<HeaderNavItem label="" font="hind" />}
          dropdownMenuItem={<HeaderNavDropdownItem label="" />}
        />
      }
      overflowVariant={hasDropdown ? 'dropdown' : 'scroll'}
      navListClassName={headerListCVA({ device })}
      role="menubar"
      activeIndex={activeIndex}
      shouldCenterActiveItem={shouldCenterActiveItem}
      {...props}
    >
      {items.map((navItem, idx) => {
        return (
          <HeaderNavItem
            outerWrapper={<li role="none" />}
            key={navItem.id ?? navItem.label}
            {...navItem}
            isCurrent={idx === activeIndex}
            font="hind"
          />
        );
      })}
    </DiNav>
  );
}

/**
 * Determines configuration based on device type, overflow handling and runtime environment.
 */
export const getHeaderNavConfig = ({
  isTv,
  overflowVariant,
  userPrefersCentering,
}: {
  isTv: boolean;
  userPrefersCentering: boolean;
  overflowVariant?: HeaderNavOverflowVariant;
}) => {
  const isMobile = !checkIsSSR() && window.innerWidth < DiveBreakpoint.Sm;
  const hasDropdown = overflowVariant === 'dropdown' && !isTv && !isMobile;
  const shouldCenterActiveItem = !isTv && isMobile && userPrefersCentering;

  return {
    hasDropdown,
    shouldCenterActiveItem,
  };
};
